import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { X, ExternalLink, Eye } from "lucide-react";
import { FaYoutube } from "react-icons/fa";
import { formatCount } from "@/lib/api";

const PlayerCtx = createContext(null);

export function PlayerProvider({ children }) {
  const [video, setVideo] = useState(null);

  const play = useCallback((v) => setVideo(v), []);
  const close = useCallback(() => setVideo(null), []);

  // ESC to close + lock body scroll while open
  useEffect(() => {
    if (!video) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [video, close]);

  return (
    <PlayerCtx.Provider value={{ play, close, current: video }}>
      {children}
      {video && <PlayerModal video={video} onClose={close} />}
    </PlayerCtx.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerCtx);
  if (!ctx) throw new Error("usePlayer must be used inside PlayerProvider");
  return ctx;
}

function PlayerModal({ video, onClose }) {
  const embedUrl =
    `https://www.youtube.com/embed/${video.video_id}` +
    `?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  const isShort = !!video.is_short;

  return (
    <div
      data-testid="player-modal"
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md animate-fade-up"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Close (top-right of viewport on mobile, top-right of card on desktop) */}
      <button
        type="button"
        onClick={onClose}
        data-testid="player-close"
        aria-label="Close player"
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 backdrop-blur text-white transition"
      >
        <X size={20} />
      </button>

      <div
        className={`w-full sm:max-w-3xl sm:rounded-2xl overflow-hidden bg-bg-surface sm:border sm:border-white/10 shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[92vh]`}
      >
        {/* Player */}
        <div
          className={`relative bg-black w-full ${
            isShort ? "aspect-[9/16] sm:aspect-video" : "aspect-video"
          }`}
        >
          <iframe
            data-testid="player-iframe"
            src={embedUrl}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>

        {/* Meta */}
        <div className="p-4 sm:p-5 overflow-y-auto">
          <h2 className="font-heading text-lg sm:text-xl font-semibold leading-snug text-balance" data-testid="player-title">
            {video.title}
          </h2>

          <div className="mt-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand to-orange-500 grid place-items-center text-white font-bold text-sm shrink-0">
              {video.channel?.charAt(0)?.toUpperCase() || "B"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-white truncate" data-testid="player-channel">{video.channel}</p>
              <p className="text-white/55 text-xs inline-flex items-center gap-1.5" data-testid="player-views">
                <Eye size={12} /> {formatCount(video.view_count)} views
                {video.duration ? <span className="ml-1">• {video.duration}</span> : null}
              </p>
            </div>
          </div>

          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="player-watch-youtube"
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-brand transition"
          >
            <FaYoutube className="text-brand" /> Watch on YouTube <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}
